/**
 * WorkdayHeaderPill.js
 *
 * Compact header pill of the "Munkanap" feature, shown in every module's header:
 * - idle    -> a small "play" chip ("Munkanap"; icon-only when `compact`),
 * - running -> a mini progress ring toward the daily target + the live `HH:MM` (orange; amber / red past the target),
 * - paused  -> an amber pause glyph + the FROZEN `HH:MM` (it does not tick while on a break).
 * Tap opens the {@link module:components/workday/WorkdaySheet} (hosted by `WorkdayOverlay`). Renders NOTHING when the
 * backend does not support the feature, before the first state arrived, or outside the provider.
 *
 * @module components/workday/WorkdayHeaderPill
 */

import React from 'react';
import {Pressable, StyleSheet, Text, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import Icon from '../management/SafeIcon';
import WorkdayRing, {RING_TONES} from './WorkdayRing';
import {BRAND, useManagementTheme, withAlpha} from '../../helpers/managementTheme';
import {useWorkday, useWorkdayLive} from '../../helpers/WorkdayContext';
import {formatHM} from '../../helpers/workdayRules';

/**
 * @param {object} props props.
 * @param {boolean} [props.compact] icon-only idle chip (tight headers such as the stack TopBar).
 * @param {boolean} [props.onDark] light-on-dark colours (gradient heroes).
 * @param {object} [props.style] extra container style.
 * @returns {JSX.Element|null} pill.
 */
export default function WorkdayHeaderPill({compact = false, onDark = false, style}) {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const {supported, loaded, state, openSheet} = useWorkday();
    const live = useWorkdayLive(15000);

    if (!supported || !loaded || !state) {
        return null;
    }

    const running = live.running;
    const paused = running && live.paused;
    const accent = paused ? '#F59E0B' : (running ? (RING_TONES[live.tone] || BRAND.base) : BRAND.base);
    const time = formatHM(live.workedSec);
    const surface = onDark ? 'rgba(255,255,255,0.16)' : m.colors.surface;
    const border = onDark ? 'rgba(255,255,255,0.28)' : (running ? withAlpha(accent, 0.45) : m.colors.border);
    const textColor = onDark ? '#FFFFFF' : m.colors.heading;

    return (
        <Pressable
            onPress={openSheet}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={paused ? t('workday_pill_a11y_paused', {time}) : (running ? t('workday_pill_a11y_running', {time}) : t('workday_pill_a11y_idle'))}
            style={({pressed}) => [styles.pill, {backgroundColor: surface, borderColor: border}, !onDark && m.shadow(1), pressed && styles.pressed, style]}
        >
            {running ? (
                <>
                    {paused ? (
                        <View style={[styles.play, {backgroundColor: withAlpha('#F59E0B', m.isDark ? 0.28 : 0.18)}]}>
                            <Icon name="pause" size={15} color={onDark ? '#FFFFFF' : '#F59E0B'} />
                        </View>
                    ) : (
                        <WorkdayRing size={22} stroke={3.5} progress={live.progress} tone={live.tone} trackColor={onDark ? 'rgba(255,255,255,0.25)' : undefined} />
                    )}
                    <Text style={[styles.time, {color: textColor}]} numberOfLines={1}>{time}</Text>
                </>
            ) : (
                <>
                    <View style={[styles.play, {backgroundColor: onDark ? 'rgba(255,255,255,0.22)' : withAlpha(BRAND.base, m.isDark ? 0.24 : 0.14)}]}>
                        <Icon name="play" size={15} color={onDark ? '#FFFFFF' : BRAND.base} />
                    </View>
                    {!compact && <Text style={[styles.label, {color: textColor}]} numberOfLines={1}>{t('workday_title')}</Text>}
                </>
            )}
        </Pressable>
    );
}

const styles = StyleSheet.create({
    pill: {height: 38, minWidth: 38, borderRadius: 999, borderWidth: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, paddingHorizontal: 7},
    play: {width: 24, height: 24, borderRadius: 12, alignItems: 'center', justifyContent: 'center'},
    time: {fontSize: 13.5, fontWeight: '900', letterSpacing: 0.2, fontVariant: ['tabular-nums'], paddingRight: 4},
    label: {fontSize: 13, fontWeight: '800', paddingRight: 6},
    pressed: {opacity: 0.75, transform: [{scale: 0.97}]},
});
